// The session axis. Root owns "who is this" and what has finished loading,
// through the session store (@aybuben/client/session/session.ts); AppLayout,
// which renders only under a present session, owns "what are they doing".
// Signed out is a route, not a state the game has to tolerate.

import type { SignInMethod } from "@aybuben/client/auth/flow.ts";
import { isGuest } from "@aybuben/client/auth/source.ts";
import type { AuthSource, AuthUser } from "@aybuben/client/auth/source.ts";
import { AppwriteContentSource } from "@aybuben/client/content/appwrite.ts";
import { AppwriteTransport } from "@aybuben/client/content/appwrite.ts";
import { ReleaseCache } from "@aybuben/client/content/release-cache.ts";
import { localDeviceStore, type DeviceStore } from "@aybuben/client/device/prefs.ts";
import type { OfflineAudio } from "@aybuben/client/offline/controller.ts";
import { readLoaderConfig } from "@aybuben/client/offline/flag.ts";
import type { CloseReason, LoaderConfig } from "@aybuben/client/offline/launch.ts";
import { rememberedUser } from "@aybuben/client/progress/local.ts";
import { createSession } from "@aybuben/client/session/session.ts";
import type { RemoteProgress } from "@aybuben/client/sync/remote.ts";
import type { ContentSource } from "@aybuben/core/content/source.ts";
import { DEFAULT_LANG } from "@aybuben/core/lang.ts";
import { ONBOARDING_VERSION } from "@aybuben/core/onboarding/version.ts";
import { usePostHog } from "@posthog/react";
import { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router";
import { useStore } from "zustand";

import AppLayout, { AccountPage, HomePage, LettersPage, PracticePage } from "./AppLayout.tsx";
import {
	CONTENT_BUCKET,
	CURRENT_RELEASE_ROW,
	DB,
	RELEASES,
	storage,
	tables,
} from "./appwrite/client.ts";
import { AuthFailurePage } from "./auth/AuthFailurePage.tsx";
import { AuthFlowPage } from "./auth/AuthFlowPage.tsx";
import AuthLayout from "./auth/AuthLayout.tsx";
import { AuthSuccessPage } from "./auth/AuthSuccessPage.tsx";
import { capture } from "./capture.ts";
import { IdbReleaseStore } from "./content/idb-release-store.ts";
import { webFetchFile } from "./content/web-fetch.ts";
import { StringsProvider } from "./i18n/index.tsx";
import { prefetchMascot } from "./mascot-assets.ts";
import { createWebOfflineAudio } from "./offline/web.ts";
import { isPostHogConfigured } from "./posthog.ts";
import { webAnalytics, webLifecycle } from "./session-web.ts";
import { LaunchLoader } from "./ui/LaunchLoader.tsx";
import { lazyPart, PartBoundary, PartFailed, usePartError } from "./ui/lazy.tsx";
import type { LegalPage as LegalPageView } from "./ui/LegalPage.tsx";
import { ListeningProvider } from "./ui/listening/context.tsx";
import { Onboarding } from "./ui/onboarding/Onboarding.tsx";
import { Shell, Splash, LoadError } from "./ui/primitives.tsx";

// Module-level, not a default parameter: a fresh ContentSource per render
// would resolve the release again. One per page load resolves it once.
const defaultSource = new AppwriteContentSource(
	new AppwriteTransport({
		tables,
		storage,
		fetchFile: webFetchFile,
		databaseId: DB,
		tableId: RELEASES,
		bucketId: CONTENT_BUCKET,
		currentRow: CURRENT_RELEASE_ROW,
	}),
	new ReleaseCache(new IdbReleaseStore()),
);

interface RootProps {
	auth: AuthSource;
	remote: RemoteProgress;
	source?: ContentSource;
	deviceStore?: DeviceStore;
	/** Injectable for tests; the browser's by default. */
	offlineAudio?: OfflineAudio;
	/** Injectable for tests; the `launch-loader` flag's by default. */
	loaderConfig?: LoaderConfig;
}

export default function Root({
	auth,
	remote,
	source = defaultSource,
	deviceStore = localDeviceStore,
	offlineAudio,
	loaderConfig: loaderConfigProp,
}: RootProps) {
	const posthog = usePostHog();
	const navigate = useNavigate();
	const location = useLocation();
	// One controller and one config per page load: the flag is read once, so a
	// value that arrives mid-launch applies on the next launch, as documented.
	const [audio] = useState(() => offlineAudio ?? createWebOfflineAudio(capture));
	const [loaderConfig] = useState(
		() => loaderConfigProp ?? readLoaderConfig(posthog, isPostHogConfigured),
	);
	// One session per mount, built from the props it first saw.
	const [session] = useState(() =>
		createSession({
			auth,
			remote,
			source,
			deviceStore,
			offlineAudio: audio,
			loaderConfig,
			lifecycle: webLifecycle,
			analytics: webAnalytics(posthog),
		}),
	);
	useEffect(() => {
		// Under test there is no network and no Cache Storage to fill.
		if (process.env.NODE_ENV !== "test") void prefetchMascot();
	}, []);
	useEffect(() => {
		session.getState().start();
		return () => session.getState().stop();
	}, [session]);
	// Read once: a launch with nobody remembered resolves to the sign-in wall, and
	// showing the duduk for that round trip would only flash it.
	const [expectApp] = useState(() => rememberedUser() !== null);
	const priorityDoneAt = useStore(audio.state, (s) => s.priorityDoneAt);
	const audioPhase = useStore(audio.state, (s) => s.phase);
	useEffect(() => {
		// Read by the e2e suite to know when a pass has finished.
		document.documentElement.dataset.audioSync = audioPhase;
	}, [audioPhase]);

	const phase = useStore(session, (s) => s.phase);
	const device = useStore(session, (s) => s.device);
	const fault = useStore(session, (s) => s.fault);
	const content = useStore(session, (s) => s.content);
	const contentError = useStore(session, (s) => s.contentError);
	const pulledFor = useStore(session, (s) => s.pulledFor);
	const firstLesson = useStore(session, (s) => s.firstLesson);
	const leftEmail = useStore(session, (s) => s.leftEmail);
	const {
		updateDevice,
		startGuest,
		linked,
		signOut,
		deleteAccount,
		setSaving,
		closeFirstLesson,
		retryContent,
	} = session.getState();

	const preLang = device?.lang ?? DEFAULT_LANG;
	useEffect(() => {
		if (phase.k !== "present") document.documentElement.lang = preLang;
	}, [phase.k, preLang]);

	const known = phase.k === "present" && pulledFor === phase.user.id;
	useEffect(() => {
		// Read by the e2e suite: the launch pass runs before the history is known, and a second follows it.
		document.documentElement.dataset.audioKnown = String(known);
	}, [known]);

	// The launch screen: once per page load, on app routes only, and never over
	// the sign-in wall or the load error, which the learner must be able to act on.
	const [launchClosed, setLaunchClosed] = useState(false);
	const [bootDoneAt, setBootDoneAt] = useState<number | null>(null);
	const booted = phase.k === "present" && !!content && !!device;
	useEffect(() => {
		if (booted && bootDoneAt === null) setBootDoneAt(Date.now());
	}, [booted]);
	// A page load that reaches the sign-in wall has had its launch: onboarding or
	// signing in afterwards goes straight to the app.
	useEffect(() => {
		if (phase.k === "absent") setLaunchClosed(true);
	}, [phase.k]);
	// Thrown at render, after the last hook: only a render-time throw reaches the
	// error boundary, which reports it to PostHog.
	if (fault) throw fault;
	const onAppRoute = !/^\/(auth|welcome|terms|privacy)(\/|$)/.test(location.pathname);
	const showLoader =
		!launchClosed &&
		!contentError &&
		onAppRoute &&
		(phase.k === "present" || (phase.k === "unknown" && expectApp));
	const closeLaunch = (reason: CloseReason, ms: number) => {
		setLaunchClosed(true);
		capture("launch_loader_closed", { reason, ms });
	};

	const signedIn = (u: AuthUser, method: SignInMethod) => {
		session.getState().signedIn(u, method);
		navigate("/", { replace: true });
	};

	const leaveGuest = async (email: string | null): Promise<void> => {
		// The session ignores anyone but a present guest; so does the navigation.
		if (phase.k !== "present" || !isGuest(phase.user)) return;
		await session.getState().leaveGuest(email);
		navigate("/auth", { replace: true });
	};

	const splash = (
		<StringsProvider lang={preLang}>
			<Shell>
				<Splash />
			</Shell>
		</StringsProvider>
	);

	return (
		<ListeningProvider>
			<div inert={showLoader}>
				<Routes>
					<Route
						path="/welcome"
						element={
							phase.k === "present" ? (
								<Navigate to="/" replace />
							) : phase.k === "unknown" || !device ? (
								splash
							) : (
								<StringsProvider lang={device.lang}>
									<Onboarding
										mode="welcome"
										lang={device.lang}
										onLang={(l) => updateDevice({ lang: l })}
										onFinish={startGuest}
										onHaveAccount={() => navigate("/auth")}
									/>
								</StringsProvider>
							)
						}
					/>

					<Route
						path="/auth"
						element={
							// Waits for the device's language rather than flashing English.
							device ? (
								<AuthLayout lang={device.lang} onLang={(l) => updateDevice({ lang: l })} />
							) : (
								splash
							)
						}
					>
						{/* Signed in already: a sign-in form here could only fail, since
				    Appwrite refuses a second session. */}
						<Route
							index
							element={
								phase.k === "present" ? (
									<Navigate to="/" replace />
								) : phase.k === "unknown" ? (
									// A signed-in visitor would otherwise glimpse a form they cannot use.
									<Splash />
								) : (
									<AuthFlowPage auth={auth} onDone={signedIn} initialEmail={leftEmail} />
								)
							}
						/>
						<Route
							path="success"
							element={
								<AuthSuccessPage signedIn={phase.k === "unknown" ? null : phase.k === "present"} />
							}
						/>
						<Route
							path="failure"
							element={
								<AuthFailurePage
									guest={phase.k === "present" && isGuest(phase.user)}
									onLeaveGuest={() => leaveGuest(null)}
								/>
							}
						/>
					</Route>

					<Route
						path="/terms"
						element={
							device ? (
								<StringsProvider lang={device.lang}>
									<Legal doc="terms" lang={device.lang} />
								</StringsProvider>
							) : (
								splash
							)
						}
					/>
					<Route
						path="/privacy"
						element={
							device ? (
								<StringsProvider lang={device.lang}>
									<Legal doc="privacy" lang={device.lang} />
								</StringsProvider>
							) : (
								splash
							)
						}
					/>

					{/* The one session gate. It reads the phase the bootstrap above already
			    resolved, so guarding a route costs no request of its own. */}
					<Route
						path="/"
						element={
							phase.k === "absent" ? (
								device ? (
									<Navigate
										to={device.onboarded < ONBOARDING_VERSION ? "/welcome" : "/auth"}
										replace
									/>
								) : (
									splash
								)
							) : phase.k === "present" && content && device ? (
								<AppLayout
									offlineAudio={audio}
									content={content}
									// Present with content loaded is exactly when the session has a store.
									store={session.getState().progress()!}
									userId={phase.user.id}
									email={phase.user.email}
									guest={isGuest(phase.user)}
									device={device}
									onDevice={updateDevice}
									pulled={pulledFor === phase.user.id}
									firstLesson={firstLesson}
									onFirstLesson={closeFirstLesson}
									auth={auth}
									onLinked={linked}
									onLeaveGuest={leaveGuest}
									onSaving={setSaving}
									onSignOut={signOut}
									onDelete={deleteAccount}
								/>
							) : phase.k === "present" && contentError ? (
								<StringsProvider lang={preLang}>
									<Shell>
										<LoadError onRetry={retryContent} />
									</Shell>
								</StringsProvider>
							) : (
								// Still resolving: the session (unknown) or the content.
								splash
							)
						}
					>
						{/* The map screens and the account page are addresses. A lesson, a
              review and a result are transient states of AppLayout, not
              places: a lesson's steps are built in memory from the learner's
              live progress, so there is no URL that could rebuild one, and the
              pre-router app kept all three in state for exactly that reason. */}
						<Route index element={<HomePage />} />
						<Route path="letters" element={<LettersPage />} />
						<Route path="practice" element={<PracticePage />} />
						<Route path="account" element={<AccountPage />} />
					</Route>

					{/* Catch-all: redirect unknown paths to home (protected) or auth */}
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</div>
			{showLoader && (
				<StringsProvider lang={preLang}>
					<LaunchLoader
						lang={preLang}
						config={loaderConfig}
						bootDoneAt={bootDoneAt}
						priorityDoneAt={priorityDoneAt}
						onClose={closeLaunch}
					/>
				</StringsProvider>
			)}
		</ListeningProvider>
	);
}

// Lazy: the legal text, in every language, is read by few visits.
const LegalPage = lazyPart(() => import("./ui/LegalPage.tsx").then((m) => m.LegalPage)).Part;

function Legal(props: Parameters<typeof LegalPageView>[0]) {
	const onError = usePartError("legal");
	return (
		<PartBoundary fallback={<PartFailed onClose={() => location.assign("/")} />} onError={onError}>
			<Suspense
				fallback={
					<Shell>
						<Splash />
					</Shell>
				}
			>
				<LegalPage {...props} />
			</Suspense>
		</PartBoundary>
	);
}
